import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, MotionValue } from 'framer-motion';

interface DockItemData {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  className?: string;
  isActive?: boolean;
}

interface DockProps {
  items: DockItemData[];
  className?: string;
  panelHeight?: number;
  baseItemSize?: number;
  magnification?: number;
  distance?: number;
}

function DockItem({
  item,
  mouseX,
  baseItemSize,
  magnification,
  distance,
}: {
  item: DockItemData;
  mouseX: MotionValue<number>;
  baseItemSize: number;
  magnification: number;
  distance: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const size = useSpring(
    useTransform(mouseX, (mx) => {
      if (!ref.current) return baseItemSize;
      const rect = ref.current.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const dist = Math.abs(mx - center);
      const mapped = 1 - Math.min(dist / distance, 1);
      return baseItemSize + (magnification - baseItemSize) * mapped * mapped;
    }),
    { mass: 0.1, stiffness: 150, damping: 12 }
  );

  return (
    <motion.div
      ref={ref}
      onClick={item.onClick}
      style={{ width: size, height: size }}
      className={`flex flex-col items-center justify-center rounded-2xl cursor-pointer relative group ${item.className ?? ''}`}
    >
      <div
        className={`w-full h-full flex items-center justify-center rounded-2xl backdrop-blur-sm transition-colors duration-200 text-white border ${
          item.isActive
            ? 'bg-primary-500/25 border-primary-500/60 shadow-lg shadow-primary-500/20'
            : 'bg-white/10 hover:bg-white/20 border-white/10 hover:border-primary-500/50'
        }`}
      >
        {item.icon}
      </div>
      {/* Active section indicator dot */}
      {item.isActive && (
        <motion.div
          layoutId="dock-active-dot"
          className="absolute -bottom-2 w-1 h-1 rounded-full bg-primary-400"
          style={{ boxShadow: '0 0 6px #667eea' }}
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      {/* Tooltip */}
      <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900/90 backdrop-blur text-white text-xs px-3 py-1.5 rounded-lg border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
        {item.label}
      </span>
    </motion.div>
  );
}

const Dock: React.FC<DockProps> = ({
  items,
  className = '',
  panelHeight = 68,
  baseItemSize = 48,
  magnification = 72,
  distance = 180,
}) => {
  const mouseX = useMotionValue(Infinity);

  return (
    <motion.div
      onMouseMove={(e) => mouseX.set(e.clientX)}
      onMouseLeave={() => mouseX.set(Infinity)}
      className={`flex items-end gap-3 px-4 rounded-2xl bg-black/30 backdrop-blur-xl border border-white/10 shadow-2xl ${className}`}
      style={{ height: panelHeight, paddingBottom: 10 }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 1, type: 'spring', stiffness: 200, damping: 25 }}
    >
      {items.map((item, i) => (
        <DockItem
          key={i}
          item={item}
          mouseX={mouseX}
          baseItemSize={baseItemSize}
          magnification={magnification}
          distance={distance}
        />
      ))}
    </motion.div>
  );
};

export default Dock;
